/**
 * Хелперы для интеграции компонентов с ProfilerManager в dev режиме
 */

import React from 'react'
import { profilerManager } from '../Managers'

/**
 * Проверяет, включен ли режим разработки
 */
export const isDevMode = (): boolean => {
    const env = (import.meta as any).env
    return env?.DEV === true || env?.MODE === 'development'
}

/**
 * Генерирует уникальный ID для компонента
 */
export const generateComponentId = (name: string, type: string, parentId?: string): string => {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substr(2, 9)
    const prefix = parentId ? `${parentId}-` : ''
    return `${prefix}${type}-${name}-${timestamp}-${random}`
}

/**
 * Хелпер для регистрации компонента в ProfilerManager
 */
export const registerComponent = (
    id: string,
    instance: any,
    metadata: {
        name: string
        type: 'component' | 'template'
        path: string
        parentId?: string
    }
): void => {
    if (!isDevMode()) return

    try {
        profilerManager.registerComponent(id, instance, metadata)
    } catch (error) {
        console.warn('Ошибка регистрации компонента в ProfilerManager:', error)
    }
}

/**
 * Хелпер для отмены регистрации компонента
 */
export const unregisterComponent = (id: string): void => {
    if (!isDevMode()) return

    try {
        profilerManager.unregisterComponent(id)
    } catch (error) {
        console.warn('Ошибка отмены регистрации компонента в ProfilerManager:', error)
    }
}

/**
 * Хелпер для обновления компонента
 */
export const updateComponent = (id: string, renderTime?: number): void => {
    if (!isDevMode()) return

    try {
        profilerManager.updateComponent(id, renderTime)
    } catch (error) {
        console.warn('Ошибка обновления компонента в ProfilerManager:', error)
    }
}

/**
 * Хелпер для добавления события компонента
 */
export const addComponentEvent = (id: string, event: string, data?: any): void => {
    if (!isDevMode()) return

    try {
        profilerManager.addComponentEvent(id, event, data)
    } catch (error) {
        console.warn('Ошибка добавления события компонента в ProfilerManager:', error)
    }
}

/**
 * Хелпер для установки статуса компонента
 */
export const setComponentStatus = (
    id: string,
    status: 'active' | 'inactive' | 'error',
    errorMessage?: string
): void => {
    if (!isDevMode()) return

    try {
        profilerManager.setComponentStatus(id, status, errorMessage)
    } catch (error) {
        console.warn('Ошибка установки статуса компонента в ProfilerManager:', error)
    }
}

/**
 * HOC для автоматической регистрации компонентов
 */
export const withProfiler = (
    Component: React.ComponentType<any>,
    metadata: {
        name: string
        type: 'component' | 'template'
        path: string
    }
) => {
    if (!isDevMode()) {
        return Component
    }

    return React.forwardRef<any, any>((props, ref) => {
        const componentId = React.useMemo(() => generateComponentId(metadata.name, metadata.type), [])

        React.useEffect(() => {
            registerComponent(
                componentId,
                {
                    props,
                    ...metadata,
                },
                metadata
            )

            return () => {
                unregisterComponent(componentId)
            }
        }, [componentId, props])

        React.useEffect(() => {
            updateComponent(componentId)
        })

        return React.createElement(Component, { ...props, ref })
    })
}

/**
 * Хук для профилирования компонентов
 */
export const useProfiler = (name: string, type: 'component' | 'template', path: string, dependencies: any[] = []) => {
    const componentId = React.useMemo(() => generateComponentId(name, type), [])

    React.useEffect(() => {
        if (!isDevMode()) return

        registerComponent(
            componentId,
            {
                name,
                type,
                path,
                dependencies,
            },
            {
                name,
                type,
                path,
            }
        )

        return () => {
            unregisterComponent(componentId)
        }
    }, [componentId, name, type, path])

    React.useEffect(() => {
        if (!isDevMode()) return
        updateComponent(componentId)
    }, dependencies)

    const addEvent = React.useCallback(
        (event: string, data?: any) => {
            addComponentEvent(componentId, event, data)
        },
        [componentId]
    )

    const setStatus = React.useCallback(
        (status: 'active' | 'inactive' | 'error', errorMessage?: string) => {
            setComponentStatus(componentId, status, errorMessage)
        },
        [componentId]
    )

    return {
        componentId,
        addEvent,
        setStatus,
    }
}
